/**
 * AddFeedback — create-feedback form aligned with the post create flow
 * used by {@link ../Posts/SpillTheTeaModal}. Uses the shared hook +
 * section primitives so media, link, title, privacy, coauthors, and
 * error/submit lifecycle stay consistent with impact/solution/action.
 *
 * Product rules honored here:
 * - Category is optional; the user can submit feedback with only media,
 *   a reference link, or text content.
 * - Validation errors never appear until the user attempts to submit,
 *   so the empty form no longer shows a red banner on open.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "config/axiosConfig";
import { createFeedback, uploadEntityMedia } from "util/posts";
import TextEditor from "components/TextEditor";
import Modal from "ui/Modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/InputSelect";
import { useAuthGate } from "hooks/useAuthGate";
import { useMediaManager } from "hooks/useMediaManager";
import { usePostFormLifecycle } from "hooks/usePostFormLifecycle";
import { usePrivacyDefaults } from "hooks/usePrivacyDefaults";
import { useCoauthorManager } from "hooks/useCoauthorManager";
import { useUrlTitlePrefill } from "hooks/useUrlTitlePrefill";
import PostFormShell from "components/Posts/PostFormShell";
import MediaSection from "components/Posts/sections/MediaSection";
import LinkField from "components/Posts/sections/LinkField";
import TitleField from "components/Posts/sections/TitleField";
import CoauthorSection from "components/Posts/sections/CoauthorSection";
import PrivacySection from "components/Posts/sections/PrivacySection";
import FormStatusMessage from "components/Posts/sections/FormStatusMessage";
import { feedbackCreateSchema } from "lib/validation/contentSchemas";
import { standardizeUrl } from "util/textUtils";
import { apiRoutes } from "util/apiRoutes";
import { normalizeApiError } from "util/normalizeApiError";
import { logger } from "lib/logger";
import type { z } from "zod";

type FeedbackCategoryChoice =
  | "Bug"
  | "Enhancement"
  | "Functionality"
  | "User Interface (UI)"
  | "User Experience (UX)"
  | "Performance"
  | "Compliance"
  | "Company"
  | "Other";

type FeedbackCategoryValue = FeedbackCategoryChoice | "";
type FeedbackFormValues = z.infer<typeof feedbackCreateSchema>;

interface Props {
  open: boolean;
  onClose: () => void;
  onFeedbackSubmitted?: (feedbackId: number) => void;
  drawerMode?: boolean;
}

const FEEDBACK_CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Bug", label: "Bug" },
  { value: "Enhancement", label: "Enhancement" },
  { value: "Functionality", label: "Functionality" },
  { value: "User Interface (UI)", label: "User Interface (UI)" },
  { value: "User Experience (UX)", label: "User Experience (UX)" },
  { value: "Performance", label: "Performance" },
  { value: "Compliance", label: "Compliance" },
  { value: "Company", label: "Company" },
  { value: "Other", label: "Other" },
];

const AddFeedback: React.FC<Props> = ({
  open,
  onClose,
  onFeedbackSubmitted,
  drawerMode = false,
}) => {
  const navigate = useNavigate();
  const { currentUser, requireAuth } = useAuthGate();
  const { privacy, setPrivacy } = usePrivacyDefaults();
  const { coauthors, addCoauthor, removeCoauthor, coauthorPairs, setCoauthors } =
    useCoauthorManager(privacy);

  const mm = useMediaManager(20);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [category, setCategory] = useState<FeedbackCategoryValue>("");
  const [referenceLink, setReferenceLink] = useState<string>("");
  const [showLink, setShowLink] = useState<boolean>(false);
  const [showTitle, setShowTitle] = useState<boolean>(false);

  const { meta, loading: isFetchingMeta } = useUrlTitlePrefill({
    linkEnabled: showLink,
    referenceLink,
    titleToggleRequiredForPrefill: true,
    titleEnabled: showTitle,
    setTitle,
  });

  const lifecycle = usePostFormLifecycle<FeedbackFormValues>({
    getValues: useCallback(
      (): FeedbackFormValues => ({
        category: category || undefined,
        showTextField: showTitle,
        showLinkField: showLink,
        title,
        description,
        referenceLink,
        mediaCount: mm.media.length,
      }),
      [
        category,
        showTitle,
        showLink,
        title,
        description,
        referenceLink,
        mm.media.length,
      ],
    ),
    schema: feedbackCreateSchema,
    isReady: !mm.isUploading,
  });

  const resetForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("");
    setReferenceLink("");
    setShowLink(false);
    setShowTitle(false);
    setCoauthors([]);
    mm.clear();
    lifecycle.setError("");
  }, [mm, lifecycle, setCoauthors]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  /**
   * Enabling the link pill auto-enables the title pill so metadata prefill has
   * somewhere to write; disabling the link clears the URL so abandoned drafts
   * don't re-submit a stale reference.
   */
  const handleLinkToggle = useCallback(() => {
    setShowLink((prev) => {
      const next = !prev;
      if (!next) {
        setReferenceLink("");
      } else {
        setShowTitle(true);
      }
      return next;
    });
  }, []);

  /** Hides the title input and clears any drafted title on toggle-off. */
  const handleTitleToggle = useCallback(() => {
    setShowTitle((prev) => {
      const next = !prev;
      if (!next) {
        setTitle("");
      }
      return next;
    });
  }, []);

  const submit = useCallback(async () => {
    if (!requireAuth()) return;
    const validationError = lifecycle.validateOrError();
    if (validationError) {
      lifecycle.setError(validationError);
      return;
    }
    if (!currentUser) return;

    lifecycle.beginSubmit();

    try {
      const finalTitle = showTitle ? title.trim() || "[no_title]" : "[no_title]";
      const link = showLink ? standardizeUrl(referenceLink) : null;

      const feedbackProperties: Record<string, unknown> = { privacy };
      if (category) {
        feedbackProperties.category = category;
      }
      if (link) {
        feedbackProperties.url = link;
      }
      if (showLink && meta) {
        feedbackProperties.reference_link_title = meta.title || finalTitle;
        feedbackProperties.reference_link_description = meta.description;
        feedbackProperties.reference_link_siteName = meta.siteName;
        feedbackProperties.reference_link_image_url = meta.image?.[0]?.url;
        feedbackProperties.reference_link_image_type = meta.image?.[0]?.type;
      }

      const newFeedback = await createFeedback({
        title: finalTitle,
        body: description.trim(),
        privacy,
        properties: feedbackProperties,
        url: link,
      });
      const newFeedbackId = newFeedback.id as number;

      if (coauthors.length > 0 && privacy !== "anonymous") {
        try {
          await api.post(apiRoutes.coauthors.request("feedback", newFeedbackId), {
            addedCoauthors: coauthorPairs,
          });
        } catch (coauthorErr) {
          logger.error("Failed to add feedback coauthors", {
            err: coauthorErr,
            feedbackId: newFeedbackId,
          });
        }
      }

      if (mm.media.length > 0) {
        mm.setIsUploading(true);
        try {
          await uploadEntityMedia(
            "feedback",
            newFeedbackId,
            mm.media.map((slot) => slot.file),
            mm.orders(),
          );
        } catch (mediaErr) {
          logger.error("Failed to upload feedback media", {
            err: mediaErr,
            feedbackId: newFeedbackId,
          });
        } finally {
          mm.setIsUploading(false);
        }
      }

      resetForm();
      onClose();

      if (drawerMode && onFeedbackSubmitted) {
        onFeedbackSubmitted(newFeedbackId);
      } else {
        navigate(`/feedback/${newFeedbackId}`);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 401) {
        return;
      }
      const normalized = normalizeApiError(err, "Failed to add feedback. Please try again.");
      lifecycle.setError(normalized);
    } finally {
      lifecycle.endSubmit();
    }
  }, [
    requireAuth,
    lifecycle,
    currentUser,
    showTitle,
    showLink,
    title,
    description,
    referenceLink,
    privacy,
    category,
    meta,
    coauthors.length,
    coauthorPairs,
    mm,
    resetForm,
    onClose,
    drawerMode,
    onFeedbackSubmitted,
    navigate,
  ]);

  const linkField = useMemo(
    () => (
      <LinkField
        id="feedback-reference-link"
        name="feedback-reference-link"
        labelText="Reference URL"
        value={referenceLink}
        onChange={setReferenceLink}
        fieldPlaceholder="https://example.com/feedback-link"
      />
    ),
    [referenceLink],
  );

  const titleField = useMemo(
    () => (
      <TitleField
        id="feedback-title"
        name="feedback-title"
        labelText="Title"
        value={title}
        onChange={setTitle}
        maxLength={300}
        loading={isFetchingMeta}
        validationState={title.trim().length > 0 ? "valid" : "none"}
      />
    ),
    [title, isFetchingMeta],
  );

  const submitDisabled = !lifecycle.canSubmit || mm.isUploading;
  const isBusy = lifecycle.isSubmitting || mm.isUploading;

  const formContent = (
    <PostFormShell intro="Share your feedback with the team. Must have media files, a reference link, additional text content, or any combination of these.">
      <MediaSection
        idPrefix="feedback"
        mm={mm}
        showLink={showLink}
        showTitle={showTitle}
        onToggleLink={handleLinkToggle}
        onToggleTitle={handleTitleToggle}
        linkField={linkField}
        titleField={titleField}
      />

      <div className="form-control">
        <Select
          value={category || undefined}
          onValueChange={(value) => setCategory(value as FeedbackCategoryValue)}
        >
          <SelectTrigger className="input-select px-3 py-2" aria-label="Feedback category">
            <SelectValue
              placeholder={
                <span className="text-eoe-text-meta dark:text-eoe-dark-text-meta">
                  Category (optional)
                </span>
              }
            />
          </SelectTrigger>
          <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
            {FEEDBACK_CATEGORY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="form-control">
        <TextEditor
          value={description}
          onChange={setDescription}
          editorType="entity"
          placeholder="Description"
        />
      </div>

      {privacy !== "anonymous" ? (
        <CoauthorSection
          coauthors={coauthors}
          onAdd={addCoauthor}
          onRemove={removeCoauthor}
        />
      ) : null}

      <PrivacySection
        privacy={privacy}
        onPrivacyChange={setPrivacy}
        miniIcon={false}
        showDropdown
      />

      <FormStatusMessage error={lifecycle.error} />
    </PostFormShell>
  );

  const actionButtons = (
    <>
      <button
        type="button"
        onClick={handleClose}
        className="btn-secondary w-full sm:w-fit py-2 px-4"
      >
        Cancel
      </button>
      <button
        type="submit"
        onClick={(e) => {
          e.preventDefault();
          void submit();
        }}
        disabled={submitDisabled}
        className={`w-full sm:w-fit py-2 px-4 ${
          submitDisabled
            ? "btn-primary-disabled opacity-50 cursor-not-allowed"
            : "btn-primary"
        }`}
      >
        {isBusy ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" aria-hidden />
          </span>
        ) : (
          "Submit"
        )}
      </button>
    </>
  );

  if (drawerMode) {
    return (
      <div className="flex flex-col h-full max-h-full overflow-y-auto overflow-x-hidden space-y-4 px-4 pb-4">
        {formContent}
        <div className="flex flex-grow flex-col pb-2 justify-end place-items-end mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
          {actionButtons}
        </div>
      </div>
    );
  }

  const footer = (
    <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
      {actionButtons}
    </div>
  );

  return (
    <Modal isOpen={open} onClose={handleClose} title="Add feedback" footer={footer} stickyFooter>
      {formContent}
    </Modal>
  );
};

export default AddFeedback;
